import Button from "@/lib/Button";
import Input from "@/lib/Input";
import { useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import type { components } from "@/lib/ApiSchema";
import {
  useAddOrganization,
  useEditOrganization,
  useFetchContacts,
} from "@/lib/queries/hooks";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import useGetShopSettings from "@/lib/queries/useGetShopSettings";
import useGetAccountingSystemOrganizations from "@/lib/queries/useGetAccountingSystemOrganizations";
import useGetOrganizationDetail from "@/lib/queries/useGetOrganizationDetail";

export default function Edit({
  organization,
  done,
}: {
  organization?: components["schemas"]["OrganizationListItemSchema"];
  done: () => void;
}) {
  const { teamSlug } = useParams();
  const contacts = useFetchContacts();
  const addOrganization = useAddOrganization();
  const editOrganization = useEditOrganization();

  const [name, setName] = useState(organization?.name ?? "");
  const [emailDomain, setEmailDomain] = useState(
    organization?.email_domain ?? "",
  );
  // const [createRfq, setCreateRfq] = useState(true);

  const { data: orgDetail } = useGetOrganizationDetail(
    teamSlug,
    organization?.id ?? "",
  );
  const queryClient = useQueryClient();

  const [selectedAccountingSystemOrgId, setSelectedAccountingSystemOrgId] =
    useState<string | null>(null);

  const shopData = useGetShopSettings(teamSlug).data;
  const hasAccountingSystem = !!shopData?.accounting_system_name;

  const {
    data: accountingSysOrgs,
    isLoading: accountingSysOrgsLoading,
    error: accountingSysOrgsError,
  } = useGetAccountingSystemOrganizations({ teamSlug });

  const valid = !!name && !!emailDomain;

  return (
    <div className="h-[90vh] max-h-[900px] w-[90vw] max-w-[900px] bg-white py-8 px-14 flex flex-col gap-16">
      <div className="flex flex-col">
        <div className="py-3 font-bold text-xl">
          {organization
            ? `Edit Customer: ${organization.name}`
            : "Create Customer"}
        </div>
        {/* <div className="flex gap-2">
          <input
            type="checkbox"
            checked={createRfq}
            onChange={(e) => setCreateRfq(e.target.checked)}
          />
          <div>Create RFQ for this customer</div>
        </div> */}
      </div>

      <div className="grow flex flex-col gap-12">
        <Input label="Business Name" value={name} onChange={setName} />
        <Input
          label="Email Domain"
          value={emailDomain}
          onChange={setEmailDomain}
        />

        {hasAccountingSystem && (
          <div className="flex flex-col gap-2">
            <div className="text-sm">
              {shopData.accounting_system_name} Customer
            </div>
            {accountingSysOrgsError ? (
              <div className="text-red-500 text-sm">
                Failed to load Customer: {accountingSysOrgsError.detail}
              </div>
            ) : (
              <Select
                placeholder={`Select ${shopData.accounting_system_name} customer`}
                isLoading={accountingSysOrgsLoading}
                options={(accountingSysOrgs ?? []).map((org) => ({
                  value: org.id,
                  label: org.name,
                }))}
                value={
                  accountingSysOrgs
                    ?.map((org) => ({ value: org.id, label: org.name }))
                    .find(
                      (opt) =>
                        opt.value ===
                        (selectedAccountingSystemOrgId ??
                          orgDetail?.accounting_system_organization_id),
                    ) ?? null
                }
                onChange={(opt) =>
                  setSelectedAccountingSystemOrgId(opt?.value ?? null)
                }
                isClearable
              />
            )}
          </div>
        )}

        {organization && (
          <div className="flex flex-col gap-2">
            <div className="text-sm">Customer Contacts:</div>
            <div className="pl-4">
              {contacts
                .filter((c) => c.organization?.id === organization.id)
                .map(({ name, email }, k) => (
                  <div key={k} className="flex gap-2 items-center">
                    <span>{name}</span>
                    <span className="text-sm">({email})</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="w-full flex gap-6 justify-end items-center py-1">
        <Button onClick={done} className="border">
          Cancel
        </Button>
        <Button primary onClick={saveEdit} disabled={!valid}>
          Save
        </Button>
      </div>
    </div>
  );

  async function saveEdit() {
    const payload = {
      name,
      email_domain: emailDomain,
      accounting_system_organization_id: selectedAccountingSystemOrgId,
    };

    if (organization) {
      editOrganization(
        { ...payload, id: organization.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [
                "get",
                "/api/teams/{team_slug}/organizations/{id}",
                {
                  params: {
                    path: {
                      team_slug: teamSlug!,
                      id: organization.id,
                    },
                  },
                },
              ],
            });
            toast.success("Customer updated");
            done();
          },
        },
      );
    } else {
      addOrganization(payload, {
        onSuccess: () => {
          toast.success("Customer added");
          done();
        },
      });
    }
  }
}
